import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AnimatePresence, motion } from 'framer-motion';
import { lazy, Suspense } from "react";
import { useAuth } from './context/AuthContext'; // Importante para la lógica de sesión

// Componentes y Páginas - Importaciones estáticas para los críticos o ligeros
import Spinner from './components/Spinner';
import CookieBanner from './components/CookieBanner';

//herramientas
import { SpeedInsights } from "@vercel/speed-insights/react"

// lazy Loads
const SearchResults = lazy(() => import("./pages/SearchResults"));
const DestinationPage = lazy(() => import('./pages/DestinationPage'));
const Login = lazy(() => import('./components/Login'));
const UserDashboard = lazy(() => import('./components/UserDashboard'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));

// Lazy Loads
const Ofertas = lazy(() => import("./pages/Ofertas"));
const Adventure = lazy(() => import("./pages/Adventure"));
const Urban = lazy(() => import("./pages/Urban"));
const Tropical = lazy(() => import("./pages/Tropical"));
const Illustrated = lazy(() => import("./pages/Illustrated"));
const Home = lazy(() => import("./pages/Home"));
const ItinerarioSarria = lazy(() => import("./pages/ItinerarioSarria"));
const NotFound = lazy(() => import("./pages/NotFound"));
const LegalPage = lazy(() => import("./pages/LegalPage"));

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
};

function PageWrapper({ children }) {
  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.3 }}
    >
      {children}
    </motion.div>
  );
}

export default function App() {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Guard de carga para evitar parpadeos de rutas privadas
  if (loading) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-white">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
        <p className="text-blue-600 font-bold tracking-widest uppercase text-[10px]">Iniciando Sistemas Evelux...</p>
      </div>
    );
  }

  return (
    <AnimatePresence mode="wait">
      <Suspense fallback={<Spinner />}>
        <Routes location={location} key={location.pathname}>
          {/* RUTAS PÚBLICAS */}
          <Route path="/" element={<PageWrapper><Home /></PageWrapper>} />
          <Route path="/itinerario-sarria" element={<PageWrapper><ItinerarioSarria /></PageWrapper>} />
          <Route path="/ofertas" element={<PageWrapper><Ofertas /></PageWrapper>} />
          <Route path="/aventura" element={<PageWrapper><Adventure /></PageWrapper>} />
          <Route path="/urbano" element={<PageWrapper><Urban /></PageWrapper>} />
          <Route path="/tropical" element={<PageWrapper><Tropical /></PageWrapper>} />
          <Route path="/ilustrado" element={<Illustrated />} />
          <Route path="/resultados" element={<SearchResults />} />
          <Route path="/destino/:slug" element={<DestinationPage />} />
          <Route path="/disclaimer" element={<Navigate to="/legal/aviso-legal" replace />} />
          <Route path="/legal/:tipo" element={<PageWrapper><LegalPage /></PageWrapper>} />

          {/* RUTA DE AUTENTICACIÓN (LOGIN) */}
          <Route
            path="/login"
            element={
              user ? <Navigate to="/dashboard" /> : (
                <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
                  <Login />
                </div>
              )
            }
          />

          {/* RUTA PRIVADA: DASHBOARD (Estilo Expedia) */}
          <Route
            path="/dashboard"
            element={
              user && !user.isAnonymous ? (
                <PageWrapper>
                  <UserDashboard user={{
                    nombre: user.displayName,
                    email: user.email,
                    foto: user.photoURL,
                    uid: user.uid
                  }} />
                </PageWrapper>
              ) : (
                <Navigate to="/login" />
              )
            }
          />

          <Route
            path="/admin-evelux"
            element={
              user?.email === "jmcgisc@gmail.com" ? (
                <AdminPanel />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          {/* 404 */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        {/* El componente de monitoreo */}
        <SpeedInsights />
        {/* Banner Global de Cookies */}
        <CookieBanner />
      </Suspense>
    </AnimatePresence>
  );
}